/**
 * Texteditor ↔ Formatierungszeichen im Mail-Text.
 *
 * Gespeichert wird kein HTML, sondern Text mit Zeichen, die der Mail-Baukasten
 * kennt ([b] [i] [mark] [farbe=rot|signal|grau] [klein] [gross], dazu das alte
 * **fett**) — damit landet nur, was das Brand-Design erlaubt, in der Mail,
 * egal was im Editor eingefügt wird. Jede Formatierung wird je Textstück
 * geschlossen, nie über einen Zeilenumbruch hinweg: Der Baukasten setzt
 * Absätze einzeln um.
 */
import { BRAND, TEXT_FARBEN, escapeHtml, type TextFarbe, type TextGroesse } from '../../lib/mailBuilder/render';

/** Markierung im Editor — dieselbe Farbe wie in der Mail. */
export const MARK_HEX = BRAND.beige;

const FONT_GROESSE: Record<TextGroesse, string> = { klein: '2', gross: '5' };

/** Gespeicherter Text → HTML für das Eingabefeld. */
export function markupZuEditorHtml(markup: string): string {
  let h = escapeHtml(markup ?? '').replace(/\*\*([^*\n]+)\*\*/g, '<b>$1</b>');
  for (let i = 0; i < 6; i++) {
    const vorher = h;
    h = h
      .replace(/\[b\]([\s\S]*?)\[\/b\]/g, '<b>$1</b>')
      .replace(/\[i\]([\s\S]*?)\[\/i\]/g, '<i>$1</i>')
      .replace(/\[mark\]([\s\S]*?)\[\/mark\]/g, `<span style="background-color: ${MARK_HEX};">$1</span>`)
      .replace(/\[farbe=(rot|signal|grau)\]([\s\S]*?)\[\/farbe\]/g, (_m, f: TextFarbe, t: string) => `<font color="${TEXT_FARBEN[f].hex}">${t}</font>`)
      .replace(/\[(klein|gross)\]([\s\S]*?)\[\/\1\]/g, (_m, g: TextGroesse, t: string) => `<font size="${FONT_GROESSE[g]}">${t}</font>`);
    if (h === vorher) break;
  }
  return h.replace(/\r?\n/g, '<br>');
}

type Format = { b: boolean; i: boolean; mark: boolean; farbe: TextFarbe | null; groesse: TextGroesse | null };
const OHNE: Format = { b: false, i: false, mark: false, farbe: null, groesse: null };

function gleich(a: Format, b: Format): boolean {
  return a.b === b.b && a.i === b.i && a.mark === b.mark && a.farbe === b.farbe && a.groesse === b.groesse;
}

/** „#6d1723", „rgb(109, 23, 35)" → [r, g, b] */
function rgb(farbe: string | null | undefined): [number, number, number] | null {
  const f = (farbe ?? '').trim().toLowerCase();
  let m = /^#([0-9a-f]{6})$/.exec(f);
  if (m) return [0, 2, 4].map((i) => parseInt(m![1].slice(i, i + 2), 16)) as [number, number, number];
  m = /^#([0-9a-f]{3})$/.exec(f);
  if (m) return [0, 1, 2].map((i) => parseInt(m![1][i] + m![1][i], 16)) as [number, number, number];
  const r = /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)$/.exec(f);
  if (r) {
    if (r[4] !== undefined && Number(r[4]) === 0) return null;
    return [Number(r[1]), Number(r[2]), Number(r[3])];
  }
  return null;
}

function naechsteFarbe(farbe: string | null | undefined): TextFarbe | null | 'standard' {
  const c = rgb(farbe);
  if (!c) return null;
  const kandidaten: [TextFarbe | 'standard', string][] = [
    ...Object.entries(TEXT_FARBEN).map(([k, v]) => [k as TextFarbe, v.hex] as [TextFarbe, string]),
    ['standard', BRAND.ink],
    ['standard', '#000000'],
    ['standard', '#333333'],
  ];
  let best: TextFarbe | 'standard' = 'standard';
  let abstand = Infinity;
  for (const [name, hex] of kandidaten) {
    const k = rgb(hex)!;
    const d = (k[0] - c[0]) ** 2 + (k[1] - c[1]) ** 2 + (k[2] - c[2]) ** 2;
    if (d < abstand) { abstand = d; best = name; }
  }
  return best;
}

function istMarkiert(farbe: string | null | undefined): boolean {
  const c = rgb(farbe);
  if (!c) return false;
  // Weiß und fast Weiß (Kopierreste) zählen nicht als Markierung.
  return !(c[0] > 245 && c[1] > 245 && c[2] > 245);
}

function groesseAus(el: HTMLElement): TextGroesse | 'normal' | null {
  const attr = el.getAttribute('size');
  if (el.tagName === 'FONT' && attr) {
    const n = Number(attr);
    if (n <= 2) return 'klein';
    if (n >= 4) return 'gross';
    return 'normal';
  }
  const fs = el.style.fontSize;
  if (!fs) return null;
  if (/small/.test(fs)) return 'klein';
  if (/large/.test(fs)) return 'gross';
  const px = parseFloat(fs);
  if (Number.isNaN(px)) return null;
  const inPx = /em$/.test(fs) ? px * 16 : px;
  if (inPx <= 14) return 'klein';
  if (inPx >= 18) return 'gross';
  return 'normal';
}

function formatAus(el: HTMLElement, vorher: Format): Format {
  const f = { ...vorher };
  const t = el.tagName;
  const st = el.style;
  if (t === 'B' || t === 'STRONG' || st.fontWeight === 'bold' || Number(st.fontWeight) >= 600) f.b = true;
  if (st.fontWeight === 'normal' || (st.fontWeight && Number(st.fontWeight) < 600)) f.b = false;
  if (t === 'I' || t === 'EM' || st.fontStyle === 'italic') f.i = true;
  if (st.fontStyle === 'normal') f.i = false;
  if (t === 'MARK') f.mark = true;
  if (st.backgroundColor) f.mark = istMarkiert(st.backgroundColor);
  const farbe = naechsteFarbe(t === 'FONT' ? el.getAttribute('color') : null) ?? naechsteFarbe(st.color);
  if (farbe) f.farbe = farbe === 'standard' ? null : farbe;
  const g = groesseAus(el);
  if (g) f.groesse = g === 'normal' ? null : g;
  return f;
}

const BLOECKE = new Set(['DIV', 'P', 'LI', 'UL', 'OL', 'H1', 'H2', 'H3', 'H4', 'BLOCKQUOTE', 'PRE']);

function zeichen(text: string, f: Format): string {
  let t = text;
  if (f.b) t = `[b]${t}[/b]`;
  if (f.i) t = `[i]${t}[/i]`;
  if (f.mark) t = `[mark]${t}[/mark]`;
  if (f.farbe) t = `[farbe=${f.farbe}]${t}[/farbe]`;
  if (f.groesse) t = `[${f.groesse}]${t}[/${f.groesse}]`;
  return t;
}

/** Inhalt des Eingabefelds → gespeicherter Text. */
export function editorZuMarkup(root: HTMLElement): string {
  // Erst Textstücke mit Formatierung sammeln, gleiche Nachbarn zusammenlegen.
  const stuecke: Array<{ text: string; f: Format } | '\n'> = [];
  const letztesIstUmbruch = () => stuecke.length === 0 || stuecke[stuecke.length - 1] === '\n';

  const gehe = (node: Node, f: Format) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const teile = (node.textContent ?? '').replace(/\u00a0/g, ' ').replace(/\u200b/g, '').split('\n');
      teile.forEach((teil, i) => {
        if (i > 0) stuecke.push('\n');
        if (teil) stuecke.push({ text: teil, f });
      });
      return;
    }
    if (!(node instanceof HTMLElement)) return;
    if (node.tagName === 'BR') {
      // Das <br> am Ende eines Blocks hält nur die leere Zeile offen.
      const p = node.parentElement;
      const platzhalter = !!p && p !== root && BLOECKE.has(p.tagName) && node === p.lastChild;
      if (!platzhalter) stuecke.push('\n');
      return;
    }
    if (node.tagName === 'SCRIPT' || node.tagName === 'STYLE') return;
    const block = BLOECKE.has(node.tagName);
    if (block && !letztesIstUmbruch()) stuecke.push('\n');
    const g = formatAus(node, f);
    node.childNodes.forEach((k) => gehe(k, g));
    if (block) stuecke.push('\n');
  };
  root.childNodes.forEach((k) => gehe(k, OHNE));

  let out = '';
  let lauf: { text: string; f: Format } | null = null;
  const schliessen = () => {
    if (lauf) out += zeichen(lauf.text, lauf.f);
    lauf = null;
  };
  for (const s of stuecke) {
    if (s === '\n') { schliessen(); out += '\n'; continue; }
    if (lauf && gleich(lauf.f, s.f)) lauf.text += s.text;
    else { schliessen(); lauf = { text: s.text, f: s.f }; }
  }
  schliessen();
  // Leerzeichen am Rand gehören nicht in die Formatierung („[b]Wort [/b]").
  return out.replace(/\n+$/, '').replace(/[ \t]+\n/g, '\n');
}
